import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  VideoCameraIcon, EllipsisVerticalIcon, EyeIcon, PencilIcon, Cog6ToothIcon, TrashIcon,
  CpuChipIcon, FolderIcon, LinkIcon,
} from '@heroicons/react/24/outline';
import ListPager from '../common/ListPager';
import { MODEL_TAG_CLASS, modelSurfaceForName } from '../../constants/modelSurfaces';
import CameraTagChip from './CameraTagChip';
import { copyText } from '../../utils/clipboard';

/**
 * Tabular camera list — shared by both dashboards' "list" view. Presentational + self-paginated.
 * Node-specific bits are injected: navigation (onView/onConfig/onHeatmap callbacks), edit/delete,
 * selection, and the model map. Row status is derived from `camera.live_pipeline` (same on both).
 */
export interface CameraListTableProps {
  cameras: any[];
  loading?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: (pageIds: string[]) => void;
  onEdit?: (camera: any) => void;
  onDelete?: (id: string) => void;
  onView?: (camera: any) => void;
  onConfig?: (camera: any) => void;
  onHeatmap?: (camera: any) => void;
  /**
   * Assign this camera's AI MODELS. The row is where an operator notices a camera is missing
   * one, so it is where the fix belongs — the Model Assignment matrix is for setting a site
   * up, not for changing one camera. Withheld without `models.assign`.
   */
  onAssignModels?: (camera: any) => void;
  /** Change which GROUPS this camera belongs to. Withheld without `groups.assign_cameras`. */
  onAssignGroups?: (camera: any) => void;
  /**
   * Resolve the camera's ULLFU RTSP URL for "Copy ULLFU URL". The URL carries the stream
   * credential, so the apps pass this for admin / super_admin only (the backend re-checks);
   * the list API itself only ever returns the encrypted value.
   */
  onFetchUllfuUrl?: (camera: any) => Promise<string>;
  /**
   * Open a model's report for one camera. `surface` is a MODEL_SURFACE_RULES key (= a
   * `/reports/<slug>` segment); the app owns the route because the cloud prefixes differ.
   * Omit and the model chips stay plain labels.
   */
  onModelReport?: (camera: any, surface: string) => void;
  /** Model names per camera id; falls back to camera.model_names. */
  cameraModelMap?: Record<string, string[]>;
  /**
   * The table owns list pagination, so callers pass the FULL list, never a pre-sliced page —
   * a pre-sliced page gets paginated twice and the footer counts only the slice. Change this
   * (e.g. to the search + filter) to send the table back to page 1.
   */
  resetKey?: string;
}

const LIST_PAGE_SIZE = 50;

function derivePipelineStatus(camera: any) {
  return camera.live_pipeline ? 'online' : 'offline';
}
function statusBadgeClasses(status: string) {
  return status === 'online'
    ? { badge: 'bg-emerald-500/10 text-emerald-500', dot: 'bg-emerald-400' }
    : { badge: 'bg-muted text-muted-foreground', dot: 'bg-muted-foreground' };
}
function StatusPill({ camera }: { camera: any }) {
  const s = derivePipelineStatus(camera);
  const cls = statusBadgeClasses(s);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${cls.badge}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${cls.dot}`} />
      {s}
    </span>
  );
}

const cid = (c: any) => c.id || c.camera_id;
const modelsFor = (c: any, map?: Record<string, string[]>) => map?.[cid(c)] || c.model_names || [];

/** A camera's tags (first few) plus a PTZ chip when it is one. */
function TagCells({ camera, max = 3 }: { camera: any; max?: number }) {
  const tags: any[] = camera.tags || [];
  if (!tags.length && camera.ptz !== true) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {camera.ptz === true && (
        <CameraTagChip
          name="PTZ"
          color="purple"
          title={camera.ptz_source === 'manual' ? 'PTZ (set by hand)' : 'PTZ (detected over ONVIF)'}
        />
      )}
      {tags.slice(0, max).map((t) => <CameraTagChip key={t.id} name={t.name} color={t.color} title={`${t.category_name}: ${t.name}`} />)}
      {tags.length > max && (
        <span
          className="inline-block rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
          title={tags.slice(max).map((t) => `${t.category_name}: ${t.name}`).join('\n')}
        >
          +{tags.length - max}
        </span>
      )}
    </div>
  );
}

export default function CameraListTable({
  cameras, loading, selectedIds, onToggleSelect, onToggleSelectAll,
  onEdit, onDelete, onView, onConfig, onHeatmap, onModelReport, cameraModelMap,
  onAssignModels, onAssignGroups, onFetchUllfuUrl, resetKey,
}: CameraListTableProps) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  // The ⋮ button the menu hangs off — kept so the menu can flip above it on the last rows.
  const anchorRef = useRef<DOMRect | null>(null);
  const [copyNotice, setCopyNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [resetKey]);
  const menuRef = useRef<HTMLDivElement>(null);

  const showSelect = !!onToggleSelect;
  const showActions = !!(onView || onEdit || onConfig || onDelete || onAssignModels || onAssignGroups || onFetchUllfuUrl);
  // Tags (and the PTZ flag) come from a node that has them — the column only appears when the
  // API sent them, so a node or proxy without the feature doesn't grow an empty column.
  const showTags = cameras.some((c) => Array.isArray(c.tags));

  useEffect(() => {
    if (!openMenuId) return;
    function close() { setOpenMenuId(null); setMenuPos(null); setConfirmDeleteId(null); }
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', close, true);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('scroll', close, true); };
  }, [openMenuId]);

  // The menu opens below its button; on the last rows that runs off the bottom of the window
  // (it is position:fixed in a portal, so nothing scrolls to reveal it). Measure it before
  // paint and flip it above the button when it doesn't fit — re-run when the delete confirm
  // changes its height.
  useLayoutEffect(() => {
    const el = menuRef.current;
    const a = anchorRef.current;
    if (!openMenuId || !el || !a) return;
    const GAP = 4, MARGIN = 8;
    const h = el.offsetHeight;
    const below = a.bottom + GAP;
    const top = below + h <= window.innerHeight - MARGIN ? below : Math.max(MARGIN, a.top - GAP - h);
    setMenuPos((p) => (p && p.top !== top ? { ...p, top } : p));
  }, [openMenuId, confirmDeleteId, menuPos]);

  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  function showNotice(ok: boolean, text: string) {
    clearTimeout(noticeTimer.current);
    setCopyNotice({ ok, text });
    noticeTimer.current = setTimeout(() => setCopyNotice(null), 2500);
  }

  async function copyUllfuUrl(camera: any) {
    try {
      const url = await onFetchUllfuUrl!(camera);
      if (await copyText(url)) showNotice(true, `ULLFU URL copied — ${camera.name || 'camera'}`);
      // Copying after a network round-trip can lose the click's user activation in some
      // browsers; hand the URL over to copy by hand rather than failing silently.
      else window.prompt('Copy the ULLFU URL:', url);
    } catch (err: any) {
      showNotice(false, err?.response?.data?.message || err?.message || 'Could not get the ULLFU URL');
    }
  }

  const totalPages = Math.ceil((cameras?.length || 0) / LIST_PAGE_SIZE);
  const currentPage = Math.min(page, totalPages || 1);
  const paginated = (cameras || []).slice((currentPage - 1) * LIST_PAGE_SIZE, currentPage * LIST_PAGE_SIZE);
  const allSelected = paginated.length > 0 && paginated.every((c) => selectedIds?.has(cid(c)));

  if (!cameras || cameras.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        {loading ? (
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand" />
        ) : (
          <>
            <p className="text-[15px] font-medium">No cameras found</p>
            <p className="mt-1 text-[13px]">Add a camera or run discovery to get started.</p>
          </>
        )}
      </div>
    );
  }

  function openMenu(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    if (openMenuId === id) { setOpenMenuId(null); setMenuPos(null); return; }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    anchorRef.current = rect;
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setOpenMenuId(id);
    setConfirmDeleteId(null);
  }

  function ModelCells({ camera }: { camera: any }) {
    const names: string[] = modelsFor(camera, cameraModelMap);
    if (names.length === 0) return <span className="text-muted-foreground">—</span>;
    return (
      <div className="flex flex-wrap gap-1 items-center">
        {names.slice(0, 2).map((m, i) => {
          // Same rule as the live wall's chips: a chip whose model owns a report opens it for
          // this camera. Heatmap is excluded — it has its own button below, not a report page.
          const surface = onModelReport ? modelSurfaceForName(m) : null;
          // Same tag as the camera cards draw, from the same constant — mid-blue ink on pale
          // blue was the pairing that had to be read twice. See MODEL_TAG_CLASS.
          const cls = `inline-block rounded-md px-2 py-0.5 text-[11px] font-semibold ${MODEL_TAG_CLASS}`;
          return surface && surface !== 'heatmap' ? (
            <button key={i} onClick={(e) => { e.stopPropagation(); onModelReport!(camera, surface); }}
              title={`Open ${m} report for this camera`} className={`${cls} transition-shadow hover:ring-sky-500 dark:hover:ring-sky-400`}>{m}</button>
          ) : (
            <span key={i} className={cls}>{m}</span>
          );
        })}
        {names.length > 2 && <span className="inline-block rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">+{names.length - 2}</span>}
        {onHeatmap && names.some((n) => n.toLowerCase().includes('heatmap')) && (
          <button onClick={(e) => { e.stopPropagation(); onHeatmap(camera); }} className="text-[10px] text-sky-700 dark:text-sky-400 hover:underline font-medium ml-1">🔥 Heatmap</button>
        )}
      </div>
    );
  }

  const portalMenu = openMenuId && menuPos && createPortal(
    <div ref={menuRef} style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 9999, maxHeight: 'calc(100vh - 16px)' }}
      className="rounded-lg border border-border bg-card shadow-xl overflow-y-auto min-w-[160px]" onMouseDown={(e) => e.stopPropagation()}>
      {(() => {
        const camera = (cameras || []).find((c) => cid(c) === openMenuId);
        if (!camera) return null;
        const close = () => { setOpenMenuId(null); setMenuPos(null); };
        return (
          <>
            {onView && (
              <button onClick={(e) => { e.stopPropagation(); close(); onView(camera); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-foreground hover:bg-muted transition-colors border-b border-border text-[13px]">
                <EyeIcon className="h-4 w-4" /><span>View Live</span>
              </button>
            )}
            {onEdit && (
              <button onClick={(e) => { e.stopPropagation(); close(); onEdit(camera); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-foreground hover:bg-muted transition-colors border-b border-border text-[13px]">
                <PencilIcon className="h-4 w-4" /><span>Edit</span>
              </button>
            )}
            {onConfig && (
              <button onClick={(e) => { e.stopPropagation(); close(); onConfig(camera); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-foreground hover:bg-muted transition-colors border-b border-border text-[13px]">
                <Cog6ToothIcon className="h-4 w-4" /><span>ONVIF Config</span>
              </button>
            )}
            {onFetchUllfuUrl && (
              <button onClick={(e) => { e.stopPropagation(); close(); copyUllfuUrl(camera); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-foreground hover:bg-muted transition-colors border-b border-border text-[13px]">
                <LinkIcon className="h-4 w-4" /><span>Copy ULLFU URL</span>
              </button>
            )}
            {/* ASSIGNMENT, between the per-camera settings above and the destructive item
                below. Both open a dialog rather than acting immediately: each one changes what
                the camera RUNS, and a menu click is too cheap for that. */}
            {onAssignModels && (
              <button onClick={(e) => { e.stopPropagation(); close(); onAssignModels(camera); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-foreground hover:bg-muted transition-colors border-b border-border text-[13px]">
                <CpuChipIcon className="h-4 w-4" /><span>AI Models…</span>
              </button>
            )}
            {onAssignGroups && (
              <button onClick={(e) => { e.stopPropagation(); close(); onAssignGroups(camera); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-foreground hover:bg-muted transition-colors border-b border-border text-[13px]">
                <FolderIcon className="h-4 w-4" /><span>Groups…</span>
              </button>
            )}
            {onDelete && (confirmDeleteId === cid(camera) ? (
              <div className="px-4 py-3">
                <p className="text-[12px] text-foreground mb-2 font-medium">Delete this camera?</p>
                <div className="flex gap-2">
                  <button onClick={(e) => { e.stopPropagation(); onDelete(cid(camera)); setConfirmDeleteId(null); close(); }} className="flex-1 rounded-md bg-destructive px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-destructive/80 transition-colors">Delete</button>
                  <button onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }} className="flex-1 rounded-md border border-border px-3 py-1.5 text-[11px] text-muted-foreground hover:bg-muted transition-colors">Cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(cid(camera)); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-destructive hover:bg-destructive/10 transition-colors text-[13px]">
                <TrashIcon className="h-4 w-4" /><span>Delete</span>
              </button>
            ))}
          </>
        );
      })()}
    </div>,
    document.body
  );

  const noticeToast = copyNotice && createPortal(
    <div role="status" style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 10000 }}
      className={`rounded-lg px-4 py-2 text-[12px] font-medium shadow-xl ${copyNotice.ok ? 'bg-foreground text-background' : 'bg-destructive text-white'}`}>
      {copyNotice.text}
    </div>,
    document.body
  );

  return (
    <>
      {portalMenu}
      {noticeToast}

      {/* Mobile cards */}
      <div className="space-y-2 md:hidden">
        {paginated.map((cam) => (
          <div key={cid(cam)} className="rounded-xl border border-border bg-card p-3 shadow-card">
            <div className="flex items-center gap-3">
              {showSelect && <input type="checkbox" checked={selectedIds?.has(cid(cam))} onChange={() => onToggleSelect!(cid(cam))} className="h-4 w-4 rounded border-border text-brand focus:ring-brand flex-shrink-0" />}
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted flex-shrink-0"><VideoCameraIcon className="h-4 w-4 text-muted-foreground" /></div>
              <div className="min-w-0 flex-1">
                <h4 className="truncate text-[13px] font-semibold text-foreground">{cam.name}</h4>
                <p className="truncate text-[11px] text-muted-foreground">{cam.ip_address || 'N/A'}</p>
              </div>
              <StatusPill camera={cam} />
            </div>
            {showTags && (cam.tags?.length > 0 || cam.ptz === true) && (
              <div className="mt-2"><TagCells camera={cam} /></div>
            )}
            <div className="flex items-center gap-3 mt-2 pt-2 border-t border-border">
              {onView && <button onClick={() => onView(cam)} className="text-[12px] text-brand hover:text-brand-dark font-medium">Live</button>}
              {onEdit && <button onClick={() => onEdit(cam)} className="text-[12px] text-muted-foreground hover:text-foreground font-medium">Edit</button>}
              {onConfig && <button onClick={() => onConfig(cam)} className="text-[12px] text-muted-foreground hover:text-foreground font-medium">Config</button>}
              {onAssignModels && <button onClick={() => onAssignModels(cam)} className="text-[12px] text-muted-foreground hover:text-foreground font-medium">Models</button>}
              {onAssignGroups && <button onClick={() => onAssignGroups(cam)} className="text-[12px] text-muted-foreground hover:text-foreground font-medium">Groups</button>}
              {onFetchUllfuUrl && <button onClick={() => copyUllfuUrl(cam)} className="text-[12px] text-muted-foreground hover:text-foreground font-medium">Copy URL</button>}
              {onDelete && <button onClick={() => onDelete(cid(cam))} className="text-[12px] text-destructive hover:text-destructive/80 font-medium">Delete</button>}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="relative w-full overflow-x-auto rounded-lg border border-border bg-card shadow-card hidden md:block">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/60 backdrop-blur-[1px] rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand" />
          </div>
        )}
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-border bg-muted/50 sticky top-0">
              {showSelect && <th className="px-4 py-3 text-left"><input type="checkbox" checked={allSelected} onChange={() => onToggleSelectAll?.(paginated.map(cid))} className="h-4 w-4 rounded border-border text-brand focus:ring-brand" /></th>}
              <th className="px-4 py-3 text-left font-semibold text-foreground">Sr No.</th>
              <th className="px-4 py-3 text-left font-semibold text-foreground">Camera Name</th>
              <th className="px-4 py-3 text-left font-semibold text-foreground">Camera ID</th>
              <th className="px-4 py-3 text-left font-semibold text-foreground">Status</th>
              <th className="px-4 py-3 text-left font-semibold text-foreground">IP Address</th>
              <th className="px-4 py-3 text-left font-semibold text-foreground">Group</th>
              {showTags && <th className="px-4 py-3 text-left font-semibold text-foreground">Tags</th>}
              <th className="px-4 py-3 text-left font-semibold text-foreground">Model</th>
              {showActions && <th className="px-4 py-3 text-center font-semibold text-foreground">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {paginated.map((camera, index) => (
              <tr key={cid(camera)} className={`border-b border-border transition-colors hover:bg-muted/30 ${index % 2 === 0 ? 'bg-card' : 'bg-muted/5'}`}>
                {showSelect && <td className="px-4 py-3"><input type="checkbox" checked={selectedIds?.has(cid(camera))} onChange={() => onToggleSelect!(cid(camera))} className="h-4 w-4 rounded border-border text-brand focus:ring-brand" /></td>}
                <td className="px-4 py-3">{(currentPage - 1) * LIST_PAGE_SIZE + index + 1}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded bg-muted">
                      {camera.snapshot_url ? <img src={camera.snapshot_url} alt={camera.name} className="h-full w-full rounded object-cover" /> : <VideoCameraIcon className="h-4 w-4 text-muted-foreground" />}
                    </div>
                    <span className="max-w-xs truncate font-medium text-foreground">{camera.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3"><span className="font-mono text-[11px] text-muted-foreground">{cid(camera)?.slice(0, 8)}</span></td>
                <td className="px-4 py-3"><StatusPill camera={camera} /></td>
                <td className="px-4 py-3"><span className="text-muted-foreground">{camera.ip_address || 'N/A'}</span></td>
                <td className="px-4 py-3">
                  {camera.groups && camera.groups.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {camera.groups.slice(0, 2).map((g: any) => <span key={g.id} className="inline-block rounded-md bg-brand/10 px-2 py-0.5 text-[11px] font-medium text-brand dark:bg-brand/20">{g.name}</span>)}
                      {camera.groups.length > 2 && <span className="inline-block rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">+{camera.groups.length - 2}</span>}
                    </div>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                {showTags && <td className="px-4 py-3"><TagCells camera={camera} /></td>}
                <td className="px-4 py-3"><ModelCells camera={camera} /></td>
                {showActions && (
                  <td className="px-4 py-3">
                    <div className="flex justify-center">
                      <button onClick={(e) => openMenu(e, cid(camera))} className="rounded-lg p-1.5 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" title="More actions">
                        <EllipsisVerticalIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Outside the desktop-only table so the mobile cards page too. */}
      <ListPager page={currentPage} pageSize={LIST_PAGE_SIZE} total={cameras.length} onPageChange={setPage} itemLabel="cameras" />
    </>
  );
}
